import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import './HorizontalSlotReel.css';

// ── Layout ──────────────────────────────────────────────────────────────────
const ITEM_GAP = 12;
const VISIBLE_ITEMS = 3;

// ── Reel behaviour ────────────────────────────────────────────────────────────
// The track is a strip of shuffled "groups". One group = the full reward pool
// shuffled (Fisher–Yates), so every prize appears exactly once per group, at a
// random position ("перемешать ряд"). Several groups stacked = the track.
// Fewer groups traveled = fewer fragments scrolled per spin = lower peak speed
// (the middle of the spin spins calmer). Bump this up for a longer, faster reel.
const LEAD_GROUPS        = 2;    // groups traveled per spin → uniform scroll length
const REST_BEHIND_GROUPS = 1;    // groups kept behind the centered item at rest
const TARGET_GROUPS      = REST_BEHIND_GROUPS + 1 + LEAD_GROUPS + 1; // behind+current+lead+last

// ── Motion tuning ─────────────────────────────────────────────────────────────
const SPIN_DURATION  = 4200;  // nominal deceleration time (ms) — bigger = slower
const SPIN_MIN       = 3600;  // clamp: fastest the stop is allowed to be
const SPIN_MAX       = 5400;  // clamp: slowest, gentlest glide to a stop (MUST be > MIN)
const ACCEL_DURATION = 800;   // ramp the wait-phase speed up from 0 (slow start)
const EASE_SLOPE0    = 4;      // d/dt of the stop easing at t=0 (ease-out quart)

// ease-out quart — long, soft tail so the reel eases to a stop gradually
const easeOutStop = (t) => 1 - Math.pow(1 - t, 4);
// ease-in — wait-phase speed builds up gently instead of snapping to full speed
const easeInRamp  = (t) => t * t;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Fisher–Yates shuffle of [0..n-1].
function shuffledIndices(n) {
  const arr = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const HorizontalSlotReel = forwardRef(({ items, onSpinEnd }, ref) => {
  const containerRef = useRef(null);
  const trackRef     = useRef(null);
  const rafRef       = useRef(null);

  const [itemWidth, setItemWidth]     = useState(125);
  const [strip, setStrip]             = useState([]);   // [{ key, poolIndex, item }]
  const [centeredKey, setCenteredKey] = useState(null);

  // Live state read inside rAF / imperative handle (avoids stale closures)
  const offsetRef        = useRef(0);                   // px the track is shifted left
  const itemWidthRef     = useRef(125);
  const wrapperWidthRef  = useRef(0);
  const stripRef         = useRef(strip);
  const poolRef          = useRef(items);
  const keyCounterRef    = useRef(0);
  const initializedRef   = useRef(false);
  const accelVelRef      = useRef(0);                   // wait-phase top speed (px/ms)
  const velRef           = useRef(0);                   // current instantaneous speed
  const spinStartRef     = useRef(0);                   // ts the wait phase started
  const appendPendingRef = useRef(false);

  // Always call the freshest onSpinEnd, even from a stale rAF closure
  const onSpinEndRef = useRef(onSpinEnd);
  useEffect(() => { onSpinEndRef.current = onSpinEnd; }, [onSpinEnd]);

  // ── Geometry helpers ─────────────────────────────────────────────────────────
  const stride  = () => itemWidthRef.current + ITEM_GAP;
  // px offset that centers strip index `i` under the picker
  const centerC = () => (wrapperWidthRef.current - itemWidthRef.current) / 2;
  const poolSize = () => (poolRef.current ? poolRef.current.length : 0);

  const applyTransform = () => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${-offsetRef.current}px,0,0)`;
    }
  };

  const cancelAnim = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  // Append `numGroups` shuffled pool-groups to `arr`, avoiding a duplicate prize
  // straddling a group boundary. Pure — returns a new array.
  const appendGroups = (arr, numGroups) => {
    const pool = poolRef.current;
    if (!pool || pool.length === 0 || numGroups <= 0) return arr;
    const out = arr.slice();
    for (let g = 0; g < numGroups; g++) {
      const order = shuffledIndices(pool.length);
      const prev = out.length ? out[out.length - 1].poolIndex : -1;
      if (order.length > 1 && order[0] === prev) {
        const k = 1 + Math.floor(Math.random() * (order.length - 1));
        [order[0], order[k]] = [order[k], order[0]];
      }
      for (const pi of order) {
        out.push({ key: keyCounterRef.current++, poolIndex: pi, item: pool[pi] });
      }
    }
    return out;
  };

  // ── (Re)build the strip whenever the pool changes ────────────────────────────
  useEffect(() => {
    poolRef.current = items;
    cancelAnim();
    if (!items || items.length === 0) {
      setStrip([]);
      return;
    }
    keyCounterRef.current = 0;
    initializedRef.current = false; // re-center on next layout
    setCenteredKey(null);
    setStrip(appendGroups([], TARGET_GROUPS));
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Measure container → derive item width ────────────────────────────────────
  useEffect(() => {
    const measure = () => {
      const w = containerRef.current?.offsetWidth || 0;
      if (!w) return;
      wrapperWidthRef.current = w;
      const iw = (w - (VISIBLE_ITEMS - 1) * ITEM_GAP) / VISIBLE_ITEMS;
      itemWidthRef.current = iw;
      setItemWidth(iw);
      if (initializedRef.current) applyTransform();
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // ── Keep refs in sync, init resting offset, paint the transform ──────────────
  useLayoutEffect(() => {
    stripRef.current = strip;
    appendPendingRef.current = false;
    if (
      !initializedRef.current &&
      wrapperWidthRef.current > 0 &&
      itemWidthRef.current > 0 &&
      strip.length > 0
    ) {
      // Rest with the first item of the "current" group centered under the picker.
      offsetRef.current = REST_BEHIND_GROUPS * poolSize() * stride() - centerC();
      initializedRef.current = true;
    }
    applyTransform();
  }, [strip, itemWidth]);

  // ── Imperative API (stable — all live state read through refs) ───────────────
  useImperativeHandle(ref, () => ({

    // Phase 1: normalize the strip (trim consumed groups on the left, refill on
    // the right so there's runway), then spin at constant velocity while waiting
    // for the backend result.
    startSpin: () => {
      cancelAnim();
      const P = poolSize();
      if (P === 0 || !trackRef.current) return;
      setCenteredKey(null);

      const st = stride();
      const groupW = P * st;

      // Where is the centered item now (group-wise)?
      const centerIndex = Math.round((offsetRef.current + centerC()) / st);
      const centerGroup = Math.max(0, Math.floor(centerIndex / P));

      // Trim whole groups that scrolled off to the left so the centered item sits
      // back at REST_BEHIND_GROUPS, then top the strip back up to TARGET_GROUPS.
      // Offset is reduced *before* the commit so the layout effect repaints the
      // trimmed DOM at the corrected position → zero visible jump.
      const removeGroups = Math.max(0, centerGroup - REST_BEHIND_GROUPS);
      if (removeGroups > 0) offsetRef.current -= removeGroups * groupW;
      flushSync(() => setStrip((s) => {
        const trimmed = removeGroups > 0 ? s.slice(removeGroups * P) : s;
        const missing = TARGET_GROUPS - trimmed.length / P;
        return missing > 0 ? appendGroups(trimmed, missing) : trimmed;
      }));
      applyTransform();

      // Top speed = the stop easing's initial velocity for a LEAD_GROUPS trip
      // (v0 = EASE_SLOPE0·D/duration) so the handoff into stopAtItem is seamless.
      accelVelRef.current = (EASE_SLOPE0 * LEAD_GROUPS * groupW) / SPIN_DURATION;
      velRef.current = 0;
      spinStartRef.current = performance.now();

      let lastTs = null;
      const tick = (ts) => {
        // Ramp the speed up from 0 over ACCEL_DURATION → gentle acceleration.
        const ramp = clamp((ts - spinStartRef.current) / ACCEL_DURATION, 0, 1);
        velRef.current = accelVelRef.current * easeInRamp(ramp);
        if (lastTs !== null) offsetRef.current += velRef.current * (ts - lastTs);
        lastTs = ts;

        // Keep at least 2 groups of runway to the right while we wait.
        const groups   = stripRef.current.length / P;
        const rightGrp = Math.floor((offsetRef.current + wrapperWidthRef.current) / groupW);
        if (!appendPendingRef.current && groups - rightGrp < 2) {
          appendPendingRef.current = true;
          setStrip((s) => appendGroups(s, 2));
        }

        applyTransform();
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    },

    // Phase 2: decelerate onto the won prize, always traveling the SAME distance
    // (LEAD_GROUPS × pool) so every spin scrolls the same amount → uniform speed.
    stopAtItem: (poolIndex) => {
      cancelAnim();
      const P = poolSize();
      if (P === 0 || !trackRef.current) return;

      const st = stride();
      const centerIndex = Math.round((offsetRef.current + centerC()) / st);
      // Fixed landing cell a whole LEAD_GROUPS×pool ahead — well off-screen to the
      // right. The backend result is dropped into exactly this cell (the rest of
      // the strip stays randomly shuffled), so travel length never varies.
      const targetIndex = centerIndex + LEAD_GROUPS * P;

      // Ensure the strip covers the landing cell + a trailing group behind it
      // (so the win sits in the penultimate group, with runway after it).
      let s = stripRef.current;
      const needLen = targetIndex + P + 1;
      if (s.length < needLen) s = appendGroups(s, Math.ceil((needLen - s.length) / P));

      // Force the landing cell to the result, keeping its React key stable.
      const targetKey = s[targetIndex].key;
      s = s.slice();
      s[targetIndex] = { key: targetKey, poolIndex, item: poolRef.current[poolIndex] };
      flushSync(() => setStrip(s));

      const startOffset  = offsetRef.current;
      const targetOffset = targetIndex * st - centerC();
      const distance     = targetOffset - startOffset;
      const startTime    = performance.now();

      // Pick a duration whose initial velocity matches the speed we're moving at
      // right now, so there's no jolt when the spin hands off into the stop.
      // If we're still ramping up (fast API), this makes the glide longer/softer.
      const vCur = velRef.current || accelVelRef.current;
      const duration = clamp((EASE_SLOPE0 * Math.abs(distance)) / vCur, SPIN_MIN, SPIN_MAX);

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1);
        const ease = easeOutStop(t); // ease-out quart: long, soft tail
        offsetRef.current = startOffset + distance * ease;
        applyTransform();

        if (t < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          rafRef.current = null;
          offsetRef.current = targetOffset;
          applyTransform();
          setCenteredKey(targetKey);
          onSpinEndRef.current?.(poolIndex);
          // Strip is normalized again on the next startSpin (trim left / refill
          // right) so there's always somewhere to crank to.
        }
      };
      rafRef.current = requestAnimationFrame(animate);
    },

    // Cancel immediately — no onSpinEnd, no highlight.
    abort: () => {
      cancelAnim();
      setCenteredKey(null);
    },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="slot-wrapper" ref={containerRef}>
      <div className="slot-track" ref={trackRef}>
        {strip.map((entry) => (
          <div
            key={entry.key}
            className={`slot-item ${entry.key === centeredKey ? 'centered' : ''}`}
            style={{
              width: `${itemWidth}px`,
              marginRight: `${ITEM_GAP}px`,
            }}
          >
            <span className="slot-item-title">{entry.item.displayLabel || entry.item.label}</span>
            <img src={entry.item.icon} alt={entry.item.displayLabel || entry.item.label} />
            <span className="slot-item-count">{entry.item.count}</span>
          </div>
        ))}
      </div>
      <div
        className="item-picker"
        style={{ width: `${itemWidth + 12}px` }}
      />
    </div>
  );
});

export default HorizontalSlotReel;
